/**
 * The wallboard.
 *
 * WHY THIS IS NOT A TILE BUILDER.
 *
 * Backup Radar's dashboard is a grid of draggable widgets - a donut of today's
 * results, a seven-day bar chart, a count of issues. It is configurable, and
 * configurable is the problem: every MSP builds the same three tiles, and the
 * three tiles answer "what percentage is green", which is a question nobody
 * acts on. A number that is 94% today and 92% tomorrow produces a feeling, not
 * a decision.
 *
 * So this screen answers a different question: WHAT SHOULD SOMEBODY DO NEXT.
 * The hero is not a chart, it is the list of machines that need a human, worst
 * first, with the client and the reason on the row - because the reason is what
 * turns "something is wrong" into "I know who to call". Everything else on the
 * screen is context for that list.
 *
 * WHAT IT IS FOR. Two things at once, which is why it is one screen and not two:
 *   - a wall display in the office, left running all day
 *   - the screen somebody opens at 7am before touching anything else
 *
 * Present mode is the same content at a size readable across a room, with the
 * chrome removed and a refresh clock. Not a separate view: a wallboard that
 * drifts from the working board is a wallboard people stop believing.
 *
 * NO NEW QUERIES. Everything here is derived from data the app has already
 * loaded for the Overview. A dashboard that doubles the read cost of the
 * product to show the same facts differently is a dashboard that gets turned
 * off when the bill arrives.
 */

import { useEffect, useMemo, useState } from 'react';
import type { Source, Status, StatusCounts, Summary, Target } from './types';
import { SOURCE_LABELS } from './types';

interface Props {
  targets: Target[];
  counts: { overall: StatusCounts; bySource: Record<Source, StatusCounts> };
  summary: Summary | null;
  changes: { name: string; client: string; from: string; to: string; at?: { toMillis?: () => number } }[];
}

const SEVERITY: Record<string, number> = { failed: 0, unknown: 1, warning: 2, paused: 3, healthy: 4 };

function ms(v: { toMillis?: () => number } | undefined | null): number {
  return v?.toMillis?.() ?? 0;
}

function age(t: Target, now: number): string {
  const at = t.lastBackup?.toMillis?.();
  if (!at) return 'never';
  const h = (now - at) / 3_600_000;
  if (h < 1) return '<1h';
  if (h < 48) return `${Math.round(h)}h`;
  return `${Math.round(h / 24)}d`;
}

export function Dashboard({ targets, counts, summary, changes }: Props) {
  const [present, setPresent] = useState(false);
  const [tick, setTick] = useState(Date.now());

  /**
   * A clock, not a re-fetch.
   *
   * The Firestore listeners already push new data the moment it changes, so a
   * wallboard needs no polling at all. What it does need is for "18h ago" to
   * stop being wrong by lunchtime, which is what this re-render is for.
   */
  useEffect(() => {
    const id = setInterval(() => setTick(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  // Escape leaves Present mode. A screen you cannot get out of without a mouse
  // is a screen somebody will reboot the display to escape.
  useEffect(() => {
    if (!present) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setPresent(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [present]);

  const now = tick;

  const attention = useMemo(
    () => targets
      .filter((t) => t.status === 'failed' || t.status === 'unknown' || t.status === 'warning')
      .sort((a, b) =>
        (SEVERITY[a.status] ?? 9) - (SEVERITY[b.status] ?? 9)
        || ms(b.lastBackup as never) - ms(a.lastBackup as never)
        || a.name.localeCompare(b.name)),
    [targets],
  );

  /**
   * Clients ranked by how much of their estate is broken, not by how many
   * machines are broken.
   *
   * A client with two machines and both down is in more trouble than one with
   * forty and three down, and a raw count says the opposite. This is the number
   * that decides who to phone first.
   */
  const byClient = useMemo(() => {
    const m = new Map<string, { name: string; bad: number; total: number }>();
    for (const t of targets) {
      if (t.status === 'paused') continue;
      const key = t.client || 'No client';
      const e = m.get(key) ?? { name: key, bad: 0, total: 0 };
      e.total++;
      if (t.status === 'failed' || t.status === 'unknown') e.bad++;
      m.set(key, e);
    }
    return [...m.values()]
      .filter((c) => c.bad > 0)
      .sort((a, b) => (b.bad / b.total) - (a.bad / a.total) || b.bad - a.bad)
      .slice(0, 6);
  }, [targets]);

  /** Transitions per day for a week - is the estate getting better or worse? */
  const week = useMemo(() => {
    const days: { label: string; broke: number; fixed: number }[] = [];
    for (let d = 6; d >= 0; d--) {
      const start = new Date(now - d * 86_400_000); start.setHours(0, 0, 0, 0);
      const end = start.getTime() + 86_400_000;
      const inDay = changes.filter((c) => {
        const at = ms(c.at);
        return at >= start.getTime() && at < end;
      });
      days.push({
        label: start.toLocaleDateString(undefined, { weekday: 'short' }),
        broke: inDay.filter((c) => c.to === 'failed').length,
        fixed: inDay.filter((c) => c.to === 'healthy').length,
      });
    }
    return days;
  }, [changes, now]);

  const peak = Math.max(1, ...week.map((d) => Math.max(d.broke, d.fixed)));
  const sources: Source[] = ['datto', 'sp', 'sc', 'msp', 'unitrends', 'email'];
  const c = counts.overall;

  return (
    <div className={'wallboard' + (present ? ' presenting' : '')}>
      <div className="wall-head">
        <div>
          <h2 className="wall-title">
            {c.failed > 0
              ? `${c.failed} ${c.failed === 1 ? 'machine needs' : 'machines need'} a human`
              : c.warning > 0
                ? `Nothing is down. ${c.warning} running late.`
                : 'Everything is backed up.'}
          </h2>
          <div className="wall-sub">
            {c.total} machines &middot; {c.healthy} healthy &middot; {c.warning} late &middot;{' '}
            {c.failed} failed &middot; {c.unknown} no data
            {c.paused > 0 && <> &middot; {c.paused} paused</>}
          </div>
        </div>
        <button className="btn" onClick={() => setPresent((p) => !p)}>
          {present ? 'Leave present mode  (Esc)' : 'Present'}
        </button>
      </div>

      <div className="wall-grid">
        {/* THE HERO. Everything else on this screen exists to give it context. */}
        <section className="wall-card wall-hero">
          <div className="wall-card-title">Needs a human, worst first</div>
          {attention.length === 0 ? (
            <div className="wall-empty">
              Nothing is failing, late or unreported.
              <span className="dim"> That is the whole job, done.</span>
            </div>
          ) : (
            <ul className="wall-list">
              {attention.slice(0, present ? 8 : 14).map((t) => (
                <li key={t.id} className={'wall-row wall-' + t.status}>
                  <span className="wall-pill">{t.status === 'unknown' ? 'no data' : t.status}</span>
                  <span className="wall-name">{t.name}</span>
                  <span className="wall-client">{t.client || 'No client'}</span>
                  <span className="wall-age">{age(t, now)}</span>
                  <span className="wall-why">{t.statusReason}</span>
                </li>
              ))}
              {attention.length > (present ? 8 : 14) && (
                <li className="wall-more dim">
                  and {attention.length - (present ? 8 : 14)} more on the Overview
                </li>
              )}
            </ul>
          )}
        </section>

        <section className="wall-card">
          <div className="wall-card-title">Clients in most trouble</div>
          {byClient.length === 0 ? (
            <div className="wall-empty dim">No client has a machine down.</div>
          ) : (
            <ul className="wall-clients">
              {byClient.map((cl) => (
                <li key={cl.name}>
                  <div className="wall-client-row">
                    <span className="wall-client-name">{cl.name}</span>
                    <span className="mono dim">{cl.bad} of {cl.total}</span>
                  </div>
                  <div className="wall-bar">
                    <i style={{ width: `${Math.round((cl.bad / cl.total) * 100)}%` }} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="wall-card">
          <div className="wall-card-title">Last seven days</div>
          {/* Broke versus fixed, not a success percentage. The question a
              manager actually has is whether this is getting better. */}
          <div className="wall-week">
            {week.map((d) => (
              <div className="wall-day" key={d.label + d.broke + d.fixed}>
                <div className="wall-bars">
                  <i className="broke" style={{ height: `${(d.broke / peak) * 100}%` }} title={`${d.broke} broke`} />
                  <i className="fixed" style={{ height: `${(d.fixed / peak) * 100}%` }} title={`${d.fixed} recovered`} />
                </div>
                <span className="wall-day-label">{d.label}</span>
              </div>
            ))}
          </div>
          <div className="wall-legend">
            <span><i className="swatch broke" /> broke</span>
            <span><i className="swatch fixed" /> recovered</span>
          </div>
        </section>

        <section className="wall-card">
          {/* A DEAD COLLECTOR LOOKS LIKE A QUIET ESTATE. This tile is the only
              thing on the screen that can tell you the board itself is lying. */}
          <div className="wall-card-title">Where the data comes from</div>
          <ul className="wall-sources">
            {sources.map((s) => {
              const sc = counts.bySource[s];
              const ok = summary?.lastSyncOk?.[s];
              const at = summary?.lastSyncedAt?.[s]?.toMillis?.();
              const mins = at ? Math.round((now - at) / 60000) : null;
              return (
                <li key={s}>
                  <span className={'wall-dot ' + (ok === false ? 'bad' : 'ok')} />
                  <span className="wall-src">{SOURCE_LABELS[s]}</span>
                  <span className="mono dim">{sc.total}</span>
                  <span className="mono dim">
                    {ok === false ? 'last sync FAILED' : mins === null ? 'never' : `${mins}m ago`}
                  </span>
                </li>
              );
            })}
          </ul>
        </section>
      </div>

      {present && (
        <div className="wall-foot dim">
          Updated {new Date(now).toLocaleTimeString()} &middot; press Esc to leave
        </div>
      )}
    </div>
  );
}

export type { Status };
